import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sanitizePlanHtml } from '../../utils/chatHtmlSanitize';
import { apiService } from '../../services/api';

export interface ChatAttachmentView {
  attachmentId: string;
  fileName: string;
  mediaType: string;
  sizeBytes: number;
  kind: string;
  webUrl?: string;
  contentUrl?: string;
  previewUrl?: string;
}

export type ChatMessageStatus = 'pending' | 'completed' | 'failed';

export type ChatMessage =
  | {
      id: string;
      role: 'ai' | 'user';
      kind: 'text';
      text: string;
      time: string;
      attachments?: ChatAttachmentView[];
      status?: ChatMessageStatus;
      error?: string;
      /** While streaming HTML plan output, show placeholder instead of raw tags. */
      htmlGenerating?: boolean;
    }
  | {
      id: string;
      role: 'ai';
      kind: 'html';
      html: string;
      time: string;
      attachments?: ChatAttachmentView[];
      status?: ChatMessageStatus;
      error?: string;
    }
  | {
      id: string;
      role: 'ai';
      kind: 'table';
      text: string;
      title: string;
      columns: string[];
      rows: (string | number)[][];
      total?: { label: string; value: string };
      time: string;
      attachments?: ChatAttachmentView[];
      status?: ChatMessageStatus;
      error?: string;
    }
  | {
      id: string;
      role: 'ai';
      kind: 'canvas';
      text: string;
      title: string;
      svg?: React.ReactNode;
      svgMarkup?: string;
      time: string;
      attachments?: ChatAttachmentView[];
      status?: ChatMessageStatus;
      error?: string;
    };

interface ChatMessageItemProps {
  message: ChatMessage;
}

const labelClass =
  'font-bold tracking-widest uppercase text-[10px] text-on-surface-variant';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function fileIconForMedia(mediaType: string): string {
  if (mediaType === 'application/pdf') return 'picture_as_pdf';
  if (mediaType.startsWith('image/')) return 'image';
  if (mediaType === 'text/csv' || mediaType.includes('spreadsheetml')) return 'table_view';
  if (mediaType === 'text/plain') return 'description';
  if (mediaType.includes('wordprocessingml')) return 'article';
  return 'attach_file';
}

const AttachmentChips: React.FC<{
  attachments: ChatAttachmentView[];
  align: 'left' | 'right';
}> = ({ attachments, align }) => {
  if (!attachments.length) return null;

  const downloadViaApi = async (att: ChatAttachmentView) => {
    try {
      await apiService.downloadChatAttachment(att.attachmentId, att.fileName);
    } catch (err) {
      console.error('Chat attachment download failed', err);
    }
  };

  return (
    <div
      className={`flex flex-wrap gap-2 max-w-full ${
        align === 'right' ? 'justify-end' : 'justify-start'
      }`}
    >
      {attachments.map((att) => {
        const isImage = att.kind === 'image' || att.mediaType.startsWith('image/');
        const claudeDownload = Boolean(att.contentUrl?.startsWith('chat/attachments/'));
        // Prefer OneDrive when present; otherwise Claude Files API download.
        const canDownload = Boolean(att.webUrl) || claudeDownload;
        const previewSrc = isImage
          ? att.previewUrl || (att.contentUrl && !att.contentUrl.startsWith('chat/') ? att.contentUrl : undefined) || att.webUrl
          : undefined;

        if (isImage && previewSrc) {
          const content = (
            <img
              src={previewSrc}
              alt={att.fileName}
              className="w-24 h-24 object-cover rounded-lg border border-outline"
              loading="lazy"
            />
          );
          return att.webUrl ? (
            <a
              key={att.attachmentId}
              href={att.webUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="block group"
              title={att.fileName}
            >
              {content}
            </a>
          ) : (
            <div key={att.attachmentId} title={att.fileName}>
              {content}
            </div>
          );
        }

        const chip = (
          <div
            className={`flex items-center gap-2.5 px-3 py-2.5 border rounded-lg max-w-[20rem] ${
              canDownload
                ? 'bg-surface-container-high border-primary/40 hover:border-primary hover:bg-primary/5'
                : 'bg-surface-container-high border-outline opacity-80'
            }`}
          >
            <span className="material-symbols-outlined text-primary text-xl shrink-0">
              {fileIconForMedia(att.mediaType)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-on-surface truncate">{att.fileName}</p>
              <p className="text-[10px] text-on-surface-variant uppercase tracking-widest">
                {formatBytes(att.sizeBytes)}
                {canDownload ? ' · Download' : ''}
              </p>
            </div>
            {canDownload && (
              <span className="material-symbols-outlined text-lg shrink-0 text-primary" aria-hidden>
                download
              </span>
            )}
          </div>
        );

        if (att.webUrl) {
          return (
            <a
              key={att.attachmentId}
              href={att.webUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              title={`Download ${att.fileName}`}
              download={att.fileName}
            >
              {chip}
            </a>
          );
        }

        if (claudeDownload) {
          return (
            <button
              key={att.attachmentId}
              type="button"
              onClick={() => void downloadViaApi(att)}
              className="rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-left"
              title={`Download ${att.fileName}`}
            >
              {chip}
            </button>
          );
        }

        return (
          <div key={att.attachmentId} title={att.fileName}>
            {chip}
          </div>
        );
      })}
    </div>
  );
};

const AiLabel: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div className="flex items-center gap-2 mb-1">
    <span className="material-symbols-outlined text-primary text-sm">{icon}</span>
    <span className={labelClass}>{label}</span>
  </div>
);

const AiHtmlBubble: React.FC<{
  html: string;
  status?: ChatMessageStatus;
  error?: string;
}> = ({ html, status, error }) => {
  const safeHtml = useMemo(() => sanitizePlanHtml(html), [html]);
  const isFailed = status === 'failed';

  return (
    <div
      className={`w-full max-w-full border-l-2 ${
        isFailed ? 'border-error' : 'border-primary'
      } rounded-r-xl rounded-bl-xl shadow-lg overflow-hidden`}
    >
      <div
        className="chat-html-report min-w-0 overflow-x-auto"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
      {isFailed && error && (
        <p className="px-4 pb-3 text-xs text-on-error-container">
          <span className="material-symbols-outlined text-[14px] align-text-bottom mr-1">
            error
          </span>
          {error}
        </p>
      )}
    </div>
  );
};

const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  h1: ({ children }) => (
    <h1 className="text-base font-bold text-on-surface mt-3 mb-1.5 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-sm font-bold text-on-surface mt-3 mb-1.5 first:mt-0 tracking-wide">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-on-surface mt-2.5 mb-1 first:mt-0">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="text-sm leading-relaxed text-on-surface mb-2 last:mb-0">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="text-sm leading-relaxed text-on-surface list-disc pl-5 mb-2 space-y-1 last:mb-0">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="text-sm leading-relaxed text-on-surface list-decimal pl-5 mb-2 space-y-1 last:mb-0">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="text-sm leading-relaxed text-on-surface">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-on-surface">{children}</strong>,
  em: ({ children }) => <em className="italic text-on-surface">{children}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="text-primary underline underline-offset-2 hover:text-primary/80"
    >
      {children}
    </a>
  ),
  code: ({ className, children }) => {
    const isBlock = Boolean(className?.includes('language-'));
    if (isBlock) {
      return (
        <code className="block text-[12px] font-mono text-on-surface whitespace-pre overflow-x-auto">
          {children}
        </code>
      );
    }
    return (
      <code className="px-1 py-0.5 rounded bg-surface-container-highest text-[12px] font-mono text-primary">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-2 p-3 rounded-lg bg-surface-container-highest border border-outline overflow-x-auto last:mb-0">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-outline pl-3 my-2 text-on-surface-variant italic">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3 border-outline/50" />,
  table: ({ children }) => (
    <div className="my-2 overflow-auto max-h-[min(60vh,28rem)] rounded-lg border border-outline last:mb-0">
      <table className="min-w-full text-left text-[12px] border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-surface-container-highest">{children}</thead>,
  th: ({ children }) => (
    <th className="px-2.5 py-1.5 font-bold uppercase tracking-widest text-[10px] text-on-surface-variant border-b border-outline">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-2.5 py-1.5 text-on-surface border-b border-outline/40 align-top">{children}</td>
  ),
};

const AiMarkdown: React.FC<{ text: string }> = ({ text }) => (
  <div className="chat-ai-markdown min-w-0">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {text}
    </ReactMarkdown>
  </div>
);

const AiTextBubble: React.FC<{
  text: string;
  status?: ChatMessageStatus;
  error?: string;
  accent?: 'primary' | 'tertiary';
  htmlGenerating?: boolean;
}> = ({ text, status, error, accent = 'primary', htmlGenerating = false }) => {
  const isPending = status === 'pending';
  const isFailed = status === 'failed';
  const borderColor = isFailed
    ? 'border-error'
    : accent === 'tertiary'
      ? 'border-tertiary'
      : 'border-primary';

  if (isPending && (!text || htmlGenerating)) {
    return (
      <div className="flex items-center gap-2 opacity-50 max-w-[90%]">
        <span className="material-symbols-outlined text-sm animate-spin">sync</span>
        <span className={labelClass}>
          {htmlGenerating ? 'Generating plan report…' : 'AI is responding...'}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`max-w-[90%] bg-surface-container border-l-2 ${borderColor} p-4 rounded-r-xl rounded-bl-xl shadow-lg`}
    >
      {text ? (
        <div className="relative">
          <AiMarkdown text={text} />
          {isPending && (
            <span className="inline-block w-1.5 h-4 ml-0.5 bg-primary align-text-bottom animate-pulse" />
          )}
        </div>
      ) : null}
      {isFailed && error && (
        <p className="mt-2 text-xs text-on-error-container">
          <span className="material-symbols-outlined text-[14px] align-text-bottom mr-1">
            error
          </span>
          {error}
        </p>
      )}
    </div>
  );
};

const UserTextBubble: React.FC<{ text: string }> = ({ text }) => (
  <div className="max-w-[85%] bg-primary p-4 rounded-l-xl rounded-br-xl shadow-md">
    <p className="text-sm leading-relaxed text-on-primary font-medium whitespace-pre-wrap">
      {text}
    </p>
  </div>
);

const TableCard: React.FC<{
  title: string;
  columns: string[];
  rows: (string | number)[][];
  total?: { label: string; value: string };
}> = ({ title, columns, rows, total }) => {
  const gridColsClass: Record<number, string> = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
  };
  const gridTemplate = gridColsClass[columns.length] ?? 'grid-cols-3';

  return (
    <div className="bg-surface-container-lowest rounded border border-outline overflow-hidden w-full">
      <div className="bg-slate-900/50 px-3 py-2 border-b border-outline">
        <span className={labelClass}>{title}</span>
      </div>
      <div className="bg-surface-container-high/40 px-3 py-2 border-b border-outline/30">
        <div className={`grid ${gridTemplate} gap-2`}>
          {columns.map((c, i) => (
            <span
              key={c}
              className={`text-[10px] font-bold uppercase tracking-widest text-on-surface-variant ${
                i === 0 ? '' : 'text-right'
              }`}
            >
              {c}
            </span>
          ))}
        </div>
      </div>
      <div className="divide-y divide-outline/30 max-h-[min(60vh,28rem)] overflow-y-auto">
        {rows.map((row, rIdx) => (
          <div key={rIdx} className={`grid ${gridTemplate} gap-2 px-3 py-2 text-[12px]`}>
            {row.map((cell, cIdx) => {
              const isFirst = cIdx === 0;
              const isLast = cIdx === row.length - 1;
              return (
                <span
                  key={cIdx}
                  className={
                    isFirst
                      ? 'text-on-surface-variant'
                      : isLast
                        ? 'text-right font-bold text-tertiary'
                        : 'text-right text-on-surface'
                  }
                >
                  {cell}
                </span>
              );
            })}
          </div>
        ))}
      </div>
      {total && (
        <div className="flex justify-between px-3 py-2 bg-primary/5 text-[12px] border-t border-outline/30">
          <span className="font-bold text-primary uppercase">{total.label}</span>
          <span className="text-tertiary font-bold tracking-tight">{total.value}</span>
        </div>
      )}
    </div>
  );
};

const DefaultFloorPlanSvg: React.FC = () => (
  <svg
    viewBox="0 0 400 220"
    xmlns="http://www.w3.org/2000/svg"
    className="w-full h-full block"
    preserveAspectRatio="xMidYMid meet"
    aria-label="Concept floor plan"
  >
    <rect width="400" height="220" fill="#0b1117" />
    <rect
      x="40"
      y="30"
      width="320"
      height="160"
      fill="none"
      stroke="#137fec"
      strokeWidth="2.5"
    />
    <line x1="200" y1="30" x2="200" y2="190" stroke="#137fec" strokeWidth="1.5" />
    <line x1="40" y1="110" x2="200" y2="110" stroke="#137fec" strokeWidth="1.5" />
    <line x1="200" y1="130" x2="360" y2="130" stroke="#137fec" strokeWidth="1.5" />
    <text x="120" y="75" textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="Inter">
      LIVING
    </text>
    <text x="120" y="155" textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="Inter">
      KITCHEN
    </text>
    <text x="280" y="115" textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="Inter">
      BEDROOM
    </text>
  </svg>
);

const CanvasCard: React.FC<{
  title: string;
  svg?: React.ReactNode;
  svgMarkup?: string;
}> = ({ title, svg, svgMarkup }) => (
  <div className="w-full max-w-[90%] bg-surface-container border-l-2 border-tertiary rounded-r-xl rounded-bl-xl shadow-lg overflow-hidden">
    <div className="h-56 w-full bg-surface relative">
      {svgMarkup ? (
        <div
          className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:block"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: svgMarkup }}
        />
      ) : (
        svg ?? <DefaultFloorPlanSvg />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-surface-container to-transparent opacity-60 pointer-events-none" />
      <div className="absolute bottom-3 left-4 flex items-center gap-2">
        <span className="material-symbols-outlined text-primary text-sm">architecture</span>
        <span className={labelClass}>{title}</span>
      </div>
    </div>
  </div>
);

const ChatMessageItem: React.FC<ChatMessageItemProps> = ({ message }) => {
  if (message.role === 'user') {
    return (
      <div className="flex flex-col gap-2 max-w-[85%] ml-auto items-end">
        {message.attachments && message.attachments.length > 0 && (
          <AttachmentChips attachments={message.attachments} align="right" />
        )}
        {message.text && <UserTextBubble text={message.text} />}
        <span className={`${labelClass} mr-2 text-slate-500`}>Sent</span>
      </div>
    );
  }

  if (message.kind === 'text') {
    return (
      <div className="flex flex-col gap-2 max-w-full w-full">
        <AiLabel icon="robot_2" label="System analysis" />
        <AiTextBubble
          text={message.text}
          status={message.status}
          error={message.error}
          htmlGenerating={message.htmlGenerating}
        />
        {message.attachments && message.attachments.length > 0 && (
          <AttachmentChips attachments={message.attachments} align="left" />
        )}
      </div>
    );
  }

  if (message.kind === 'html') {
    return (
      <div className="flex flex-col gap-2 w-full max-w-full">
        <AiLabel icon="architecture" label="Plan analysis" />
        <AiHtmlBubble html={message.html} status={message.status} error={message.error} />
        {message.attachments && message.attachments.length > 0 && (
          <AttachmentChips attachments={message.attachments} align="left" />
        )}
      </div>
    );
  }

  if (message.kind === 'table') {
    return (
      <div className="flex flex-col gap-2 max-w-[90%]">
        <AiLabel icon="architecture" label="Architectural delta" />
        <div className="bg-surface-container border-l-2 border-tertiary p-4 rounded-r-xl rounded-bl-xl shadow-lg space-y-4">
          {message.text && <AiMarkdown text={message.text} />}
          <TableCard
            title={message.title}
            columns={message.columns}
            rows={message.rows}
            total={message.total}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 max-w-[90%]">
      <AiLabel icon="architecture" label="Site plan" />
      {message.text && (
        <AiTextBubble text={message.text} status={message.status} error={message.error} accent="tertiary" />
      )}
      <CanvasCard title={message.title} svg={message.svg} svgMarkup={message.svgMarkup} />
    </div>
  );
};

export default ChatMessageItem;
